import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../hooks/useTheme';
import { useCart } from '../contexts/CartContext';
import { useCartActions } from '../hooks/useCartActions';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { useAuth } from '../contexts/AuthContext';
import AuthModal from '../components/landing/AuthModal';

// Components
import Header from '../components/landing/Header';
import Footer from '../components/landing/Footer';
import ProductImage from '../components/landing/ProductImage';

const CartPage = () => {
    const { theme, toggleTheme } = useTheme();
    const { items, total } = useCart();
    const { updateCartQuantity, removeFromCart, formatPrice } = useCartActions();
    const navigate = useNavigate();

    // Configuración de envío desde variable de entorno
    const freeShippingThreshold = parseFloat(process.env.REACT_APP_FREE_SHIPPING_THRESHOLD || '500');
    const shippingCost = parseFloat(process.env.REACT_APP_SHIPPING_COST || '15');
    
    // Calcular costos (precios ya incluyen IGV)
    const subtotal = total;
    const shipping = subtotal >= freeShippingThreshold ? 0 : shippingCost;
    const igvIncluido = subtotal * 0.18 / 1.18; // IGV estimado incluido en precios
    const finalTotal = subtotal + shipping; // NO sumar IGV nuevamente

    const [authModalVisible, setAuthModalVisible] = useState(false);
    const { isAuthenticated, loading: authLoading } = useAuth();

    const handleContinueShopping = () => {
        navigate('/catalogo');
    };

    const handleCheckout = () => {
        if (!isAuthenticated && !authLoading) {
            setAuthModalVisible(true);
        } else {
            navigate('/checkout');
        }
    };

    const handleAuthSuccess = () => {
        setAuthModalVisible(false);
        setTimeout(() => navigate('/checkout'), 300);
    };

    // Opciones de cantidad por item se generan dinámicamente según stock

    return (
        <div className={`landing-page theme-${theme}`}>
            <Helmet>
                <title>Carrito de Compras - WINS Technology</title>
                <meta name="description" content="Revisa los productos en tu carrito de compras y procede al checkout en WINS Technology." />
                <meta property="og:title" content="Carrito de Compras - WINS Technology" />
                <meta property="og:description" content="Revisa los productos en tu carrito de compras y procede al checkout." />
                <meta property="og:type" content="website" />
                <link rel="canonical" href={`${process.env.REACT_APP_SITE_URL}/carrito`} />
            </Helmet>

            <Header theme={theme} onToggleTheme={toggleTheme} />
            
            <main className="landing-main">
                {items.length === 0 ? (
                    <div className="surface-section px-4 py-8 md:px-6 lg:px-8">
                        <div className="text-center py-8">
                            <i className="pi pi-shopping-cart text-6xl text-color-secondary mb-4"></i>
                            <h2 className="text-2xl font-semibold mb-4">Tu carrito está vacío</h2>
                            <p className="text-color-secondary mb-6">
                                Explora nuestro catálogo y encuentra los productos que necesitas
                            </p>
                            <Button 
                                label="Continuar Comprando"
                                icon="pi pi-shopping-bag"
                                className="p-button-primary"
                                onClick={handleContinueShopping}
                            />
                        </div>
                    </div>
                ) : (
                    <div className="surface-section px-4 py-8 md:px-6 lg:px-8">
                        <div className="flex flex-column align-items-center mb-6">
                            <div className="text-900 text-4xl mb-4 font-medium">
                                Total de tu carrito {formatPrice(finalTotal)}
                            </div>
                            <p className="text-600 font-medium text-xl mt-0 mb-4">
                                {shipping === 0 ? 'ENVÍO GRATIS' : `ENVÍO ${formatPrice(shipping)}`}
                            </p>
                            <Button 
                                label="Proceder al Checkout" 
                                icon="pi pi-credit-card"
                                className="p-button-success p-button-lg"
                                onClick={handleCheckout}
                            />
                        </div>

                        <ul className="list-none p-0 m-0">
                            {items.map((item, index) => (
                                <li key={item.id} className="flex flex-column md:flex-row py-3 border-top-1 border-bottom-1 surface-border md:align-items-center">
                                    <ProductImage 
                                        src={item.imagen} 
                                        alt={item.nombre}
                                        className="w-12rem flex-shrink-0 mx-auto md:mx-0"
                                        style={{ maxWidth: '100%' }}
                                    />
                                    <div className="flex-auto py-2 md:pl-5">
                                        <div className="flex flex-wrap align-items-start sm:align-items-center sm:flex-row sm:justify-content-between surface-border pb-6">
                                            <div className="w-full sm:w-6 flex flex-column">
                                                <span className="text-900 text-xl font-medium mb-3">
                                                    {item.nombre}
                                                </span>
                                                <span className="text-600">
                                                    Precio unitario: {formatPrice(item.precio_venta)}
                                                </span>
                                            </div>
                                            <div className="w-full sm:w-6 flex align-items-start justify-content-between mt-3 sm:mt-0">
                                                <div>
                                                    <Dropdown 
                                                        options={Array.from({ length: Math.min(10, item.stock_disponible || 10) }, (_, i) => ({ label: (i + 1).toString(), value: i + 1 }))} 
                                                        value={item.cantidad} 
                                                        onChange={(e) => updateCartQuantity(item.id, e.value, item.stock_disponible, item.nombre)} 
                                                        placeholder="Cantidad"
                                                    />
                                                </div>
                                                <div className="flex flex-column sm:align-items-end">
                                                    <span className="text-900 text-2xl font-medium mb-2 sm:mb-3">
                                                        {formatPrice(item.precio_venta * item.cantidad)}
                                                    </span>
                                                    <a 
                                                        className="cursor-pointer text-pink-500 font-medium hover:text-pink-600 transition-colors transition-duration-300" 
                                                        tabIndex="0"
                                                        onClick={() => removeFromCart(item.id, item.nombre)}
                                                    >
                                                        Eliminar
                                                    </a>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex flex-column">
                                            <span className="inline-flex align-items-center mb-3">
                                                <i className="pi pi-envelope text-600 mr-2"></i>
                                                <span className="text-600 mr-2">Ordena hoy.</span>
                                            </span>
                                            <span className="inline-flex align-items-center mb-3">
                                                <i className="pi pi-send text-600 mr-2"></i>
                                                <span className="text-600 mr-2">
                                                    Entrega estimada <span className="font-bold">1-3 días hábiles después de confirmado el pago.</span>
                                                </span>
                                            </span>
                                            <span className="flex align-items-center">
                                                <i className="pi pi-check-circle text-600 mr-2"></i>
                                                <span className="text-600">
                                                    {item.stock_disponible > 10 
                                                        ? 'En stock' 
                                                        : `Solo ${item.stock_disponible} disponibles.`
                                                    }
                                                </span>
                                            </span>
                                        </div>
                                    </div>
                                </li>
                            ))}
                        </ul>

                        {/* Resumen de costos */}
                        <div className="flex">
                            <div className="w-12rem hidden md:block"></div>
                            <ul className="list-none py-0 pr-0 pl-0 md:pl-5 mt-6 mx-0 mb-0 flex-auto">
                                <li className="flex justify-content-between mb-4">
                                    <span className="text-xl text-900">Subtotal</span>
                                    <span className="text-xl text-900">{formatPrice(subtotal)}</span>
                                </li>
                                <li className="flex justify-content-between mb-4">
                                    <span className="text-xl text-900">Envío</span>
                                    <span className="text-xl text-900">
                                        {shipping === 0 ? 'Gratis' : formatPrice(shipping)}
                                    </span>
                                </li>
                                <li className="flex justify-content-between mb-4">
                                    <span className="text-xl text-900">IGV incluido (estimado)</span>
                                    <span className="text-xl text-900">{formatPrice(igvIncluido)}</span>
                                </li>
                                <li className="flex justify-content-between border-top-1 surface-border mb-4 pt-4">
                                    <span className="text-xl text-900 font-bold text-3xl">Total</span>
                                    <span className="text-xl text-900 font-bold text-3xl">{formatPrice(finalTotal)}</span>
                                </li>
                                <li className="flex justify-content-end gap-3">
                                    <Button 
                                        label="Continuar Comprando" 
                                        icon="pi pi-shopping-bag"
                                        className="p-button-outlined"
                                        onClick={handleContinueShopping}
                                    />
                                    <Button 
                                        label="Proceder al Checkout" 
                                        icon="pi pi-credit-card"
                                        className="p-button-success"
                                        onClick={handleCheckout}
                                    />
                                </li>
                            </ul>
                        </div>

                        {/* Información adicional */}
                        {shipping > 0 && (
                            <div className="mt-6 p-4 bg-blue-50 border-round">
                                <div className="flex align-items-center">
                                    <i className="pi pi-info-circle text-blue-600 mr-2"></i>
                                    <span className="text-blue-800">
                                        ¡Agrega {formatPrice(freeShippingThreshold - subtotal)} más para obtener envío gratis!
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </main>
            
            <Footer />
            {/* Modal de autenticación */}
            <AuthModal 
                visible={authModalVisible} 
                onHide={() => setAuthModalVisible(false)} 
                onAuthSuccess={handleAuthSuccess} 
            />
        </div>
    );
};

export default CartPage;
